<template>
  <div>
    <h2 class="content-block">Match #{{ id }}</h2>
    <div class="dx-fieldset">
      <div class="dx-fieldset-header">Match Information</div>
      <div class="dx-field">
        <div class="dx-field-label">Id</div>
        <div class="dx-field-value">{{ id }}</div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Created</div>
        <div class="dx-field-value">
          {{ formatLongDate(match.created) }}
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Created by</div>
        <div class="dx-field-value">
          <user-details :user-id="match.userIdCreated" empty-text="Automatic" />
        </div>
      </div>
      <div v-if="match.deleted !== null" class="dx-field">
        <div class="dx-field-label">Deleted</div>
        <div class="dx-field-value">
          {{ formatLongDate(match.deleted) }}
        </div>
      </div>
      <div v-if="match.deleted !== null" class="dx-field">
        <div class="dx-field-label">Deleted by</div>
        <div class="dx-field-value">
          <user-details :user-id="match.userIdDeleted" empty-text="Automatic" />
        </div>
      </div>
      <div v-if="match.deleted === null" class="dx-field">
        <div class="dx-field-label">
          <dx-button icon="trash" text="Delete" @click="deleteMatch" />
        </div>
      </div>
    </div>

    <h3 class="content-block">Rows</h3>
    <dx-data-grid
      :data-source="dataSourceRows"
      :show-borders="true"
      :remote-operations="false"
      :allow-column-reordering="false"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :word-wrap-enabled="true"
      :column-auto-width="true"
      :show-row-lines="true"
      :show-column-lines="true"
      class="dx-card wide-card"
      width="100%"
      @row-click="showRow"
    >
      <dx-column-chooser :enabled="false" />
      <dx-export
        :enabled="true"
        :file-name="`${currentDate}-rows-for-match-${id}`"
      />
      <dx-paging :page-size="pageSizes[0]" />
      <dx-pager
        :allowed-page-sizes="pageSizes"
        :show-page-size-selector="true"
        :show-navigation-buttons="true"
        :show-info="true"
      />
      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="false" />
      <dx-filter-panel :visible="true" />
      <dx-column data-field="id" :format="config.idFormat" />
      <dx-column
        data-field="comparisonSource"
        :lookup="comparisonSourceColumnLookup"
        caption="Comparison source"
      />
      <dx-column
        data-field="idAtComparisonSource"
        caption="Comparison Source Id"
      />
      <dx-column data-field="idAtGateway" caption="Gateway Id" />
      <dx-column
        data-field="timestamp"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
        caption="Gateway Timestamp"
      />
      <dx-column
        data-field="currencyId"
        :lookup="currencyColumnLookup"
        caption="Currency"
      />
      <dx-column data-field="amount" :format="config.financialNumberFormat" />
      <dx-column
        data-field="rowType"
        :lookup="rowTypeColumnLookup"
        caption="Type"
      />
      <dx-column data-field="reversal" true-text="Yes" false-text="No" />
      <dx-column data-field="counterParty" />
    </dx-data-grid>

    <h3 class="content-block">Adjustments</h3>
    <dx-data-grid
      :data-source="dataSourceAdjustments"
      :show-borders="true"
      :remote-operations="false"
      :allow-column-reordering="false"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :word-wrap-enabled="true"
      :column-auto-width="true"
      :show-row-lines="true"
      :show-column-lines="true"
      class="dx-card wide-card"
      width="100%"
    >
      <dx-column-chooser :enabled="false" />
      <dx-export
        :enabled="true"
        :file-name="`${currentDate}-adjustments-for-match-${id}`"
      />
      <dx-paging :page-size="pageSizes[0]" />
      <dx-pager
        :allowed-page-sizes="pageSizes"
        :show-page-size-selector="true"
        :show-navigation-buttons="true"
        :show-info="true"
      />
      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="false" />
      <dx-filter-panel :visible="true" />
      <dx-column data-field="id" :format="config.idFormat" />
      <dx-column
        data-field="comparisonSource"
        :lookup="comparisonSourceColumnLookup"
        caption="Comparison Source"
      />
      <dx-column data-field="amount" :format="config.financialNumberFormat" />
      <dx-column
        data-field="currencyId"
        :lookup="currencyColumnLookup"
        caption="Currency"
      />
      <dx-column data-field="comment" />
      <dx-column
        data-field="created"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
      <dx-column
        data-field="userIdCreated"
        :lookup="userColumnLookup"
        caption="Created by"
      />
      <dx-column
        data-field="approved"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
      <dx-column
        data-field="userIdApproved"
        :lookup="userColumnLookup"
        caption="Approved by"
      />
    </dx-data-grid>
  </div>
</template>

<script>
	import {mapState} from 'vuex'
	import {
		DxColumn,
		DxColumnChooser,
		DxDataGrid,
		DxExport,
		DxFilterPanel,
		DxFilterRow,
		DxHeaderFilter,
		DxPager,
		DxPaging,
	} from 'devextreme-vue/data-grid'
	import {DxButton} from 'devextreme-vue/button'
	import {DxTagBox} from 'devextreme-vue'
	import {formatDate} from 'devextreme/localization'
	import {http} from '@/utils/http'
	import {config} from '@/config'
	import UserDetails from '@/components/user-details'
	import {showHttpError} from '../../utils/http'
	import {show} from '@/utils/show'

	export default {
  name: 'ViewMatch',
  components: {
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxExport,
    DxFilterRow,
    DxFilterPanel,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxButton,
    // eslint-disable-next-line vue/no-unused-components
    DxTagBox,
    UserDetails,
  },
  data() {
    return {
      id: this.$route.params.id,
      config,
      dataSourceRows: null,
      dataSourceAdjustments: null,
      pageSizes: config.itemsPerPage,
      match: {
        created: null,
        userIdCreated: null,
        deleted: null,
        userIdDeleted: null,
      },
    }
  },
  computed: {
    ...mapState('user', [
      'statementFormatColumnLookup',
      'statementSourceColumnLookup',
      'comparisonSourceColumnLookup',
      'currencyColumnLookup',
      'userLookup',
      'userColumnLookup',
      'rowTypeColumnLookup',
    ]),
    currentDate() {
      return new Date().toISOString().split('T')[0]
    },
  },
  created() {
    this.loadData()
  },
  destroyed() {
    this.dataSourceRows && this.dataSourceRows.dispose()
  },
  methods: {
    formatLongDate(timeInMillis) {
      return (
        timeInMillis &&
        formatDate(new Date(timeInMillis), this.config.intlDateTimeFormat)
      )
    },
    async loadData() {
      if (this.dataSourceRows) {
        this.dataSourceRows.dispose()
      }

      const response = await http
        .get('reconciliation/admin/match/' + this.id)
        .json()

      this.match = response.match

      this.dataSourceRows = response.rows
      this.dataSourceAdjustments = response.adjustments
    },
    showRow(event) {
      this.$router.push({ path: `/row/${event.data.id}` })
    },
    deleteMatch() {
      http
        .post('reconciliation/admin/match/delete', {
          json: {
            id: this.id,
          },
        })
        .then(() => {
          show.success('Match deleted')
          this.loadData()
        })
        .catch((e) => {
          showHttpError(e)
        })
    },
  },
}
</script>
<style lang="scss">
td[role='columnheader'] {
  text-align: center !important;
}
</style>
